// ============================================
// Create/Edit Sub-Account Page
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { subAccountApi } from '../utils/api';
import { Loader } from '../components/Loader';
import {
  Save,
  AlertCircle,
  CheckCircle,
  Bot,
  MessageSquare,
  Key,
  Eye,
  EyeOff,
} from 'lucide-react';
import type { CampaignConfig, SubAccountCreate } from '../types';

// Form data type with all fields as strings (not optional)
type FormData = Required<SubAccountCreate>;

const SecretInput = ({
  label,
  value,
  onChange,
  showPassword,
  onToggleShow,
  placeholder,
  helpText,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  showPassword: boolean;
  onToggleShow: () => void;
  placeholder: string;
  helpText?: string;
}) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <div className="relative">
      <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
      <input
        type={showPassword ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-10 pr-12 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
      >
        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
    {helpText && <p className="mt-1 text-sm text-muted-foreground">{helpText}</p>}
  </div>
);

const Section = ({ title, icon: Icon, children, description }: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  description?: string;
}) => (
  <div className="bg-card border border-border rounded-xl p-6">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="h-5 w-5 text-primary" />
      <h3 className="text-lg font-semibold">{title}</h3>
    </div>
    {description && <p className="text-sm text-muted-foreground mb-4">{description}</p>}
    {children}
  </div>
);


export function SubAccountFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    slug: '',
    description: '',
    // GHL
    ghl_location_id: '',
    ghl_api_key: '',
    ghl_pit: '',
    ghl_form_id: '',
    ghl_custom_field_form_submitted: 'form_submitted',
    ghl_custom_field_ig_username: 'instagram_username',
    // Agent controls and knowledge
    agent_goal: 'Qualify interested prospects and guide them to the next appropriate step.',
    agent_behavior: 'Be natural, concise, and helpful. Ask one question at a time. Never invent facts or make commitments. Escalate sensitive or uncertain cases to a human.',
    bot_context: '',
    campaign_configs: [],
    lead_context: "Saw our ad and wants more info",
    qual_question_1: "What's your monthly budget for this?",
    qual_question_2: "What's your timeline to get started?",
    qual_question_3: "Where are you located?",
    qual_question_4: "What's your role/title?",
    form_url: '',
    // Behavior
    lead_timeout_minutes: 60,
    max_messages_per_run: 50,
  });

  // Password visibility toggles
  const [showGhlApiKey, setShowGhlApiKey] = useState(false);
  const [showGhlPit, setShowGhlPit] = useState(false);

  useEffect(() => {
    if (isEditing) {
      loadSubAccount();
    }
  }, [id]);

  const loadSubAccount = async () => {
    try {
      setLoading(true);
      const response = await subAccountApi.getDetail(id!);
      const sa = response.data;
      setFormData({
        name: sa.name,
        slug: sa.slug,
        description: sa.description || '',
        ghl_location_id: sa.ghl_location_id || '',
        ghl_api_key: sa.ghl_api_key || '',
        ghl_pit: sa.ghl_pit || '',
        ghl_form_id: sa.ghl_form_id || '',
        ghl_custom_field_form_submitted: sa.ghl_custom_field_form_submitted,
        ghl_custom_field_ig_username: sa.ghl_custom_field_ig_username,
        agent_goal: sa.agent_goal || 'Qualify interested prospects and guide them to the next appropriate step.',
        agent_behavior: sa.agent_behavior || 'Be natural, concise, and helpful. Ask one question at a time. Never invent facts or make commitments. Escalate sensitive or uncertain cases to a human.',
        bot_context: sa.bot_context || '',
        campaign_configs: sa.campaign_configs || [],
        lead_context: sa.lead_context,
        qual_question_1: sa.qual_question_1,
        qual_question_2: sa.qual_question_2,
        qual_question_3: sa.qual_question_3,
        qual_question_4: sa.qual_question_4,
        form_url: sa.form_url || '',
        lead_timeout_minutes: sa.lead_timeout_minutes,
        max_messages_per_run: sa.max_messages_per_run,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load sub-account');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Prepare data for API
      const configuredCampaigns = formData.campaign_configs.filter(campaign =>
        campaign.trigger_phrase.trim() || campaign.ai_context.trim() || campaign.form_url.trim() || campaign.ghl_form_id.trim()
      );
      const incompleteCampaign = configuredCampaigns.some(campaign =>
        !campaign.trigger_phrase.trim() || !campaign.ai_context.trim() || !campaign.form_url.trim() || !campaign.ghl_form_id.trim()
      );
      if (incompleteCampaign) {
        throw new Error('Complete the trigger phrase, campaign instructions, form URL, and GHL Form ID, or remove the unfinished campaign.');
      }
      const {
        ghl_form_id: _legacyGhlFormId,
        lead_context: _legacyLeadContext,
        qual_question_1: _legacyQuestion1,
        qual_question_2: _legacyQuestion2,
        qual_question_3: _legacyQuestion3,
        qual_question_4: _legacyQuestion4,
        form_url: _legacyFormUrl,
        ...currentFields
      } = formData;
      const apiData = { ...currentFields, campaign_configs: configuredCampaigns };
      if (!isEditing) {
        // For new accounts, we need all required fields
        if (!apiData.name || !apiData.slug) {
          throw new Error('Name and slug are required');
        }
      }

      if (isEditing) {
        await subAccountApi.update(id!, apiData);
      } else {
        const created = await subAccountApi.create(apiData);
        navigate(`/sub-account/${created.data.id}/edit`);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save sub-account');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader fullScreen />;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link to="/" className="text-sm text-muted-foreground hover:underline mb-2 inline-block">
            ← Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold">{isEditing ? 'Edit Sub-Account' : 'Create Sub-Account'}</h1>
          <p className="text-muted-foreground">
            {isEditing
              ? 'Update the client business configuration'
              : 'Set up a client business and connect its GoHighLevel location'}
          </p>
        </div>
        <button
          type="submit"
          form="sub-account-form"
          disabled={saving}
          className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader size="sm" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>{isEditing ? 'Update' : 'Create'}</span>
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm">Sub-account {isEditing ? 'updated' : 'created'} successfully! Redirecting...</p>
        </div>
      )}

      <form id="sub-account-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Section
          title="Basic Information"
          icon={MessageSquare}
          description="Identify this sub-account (client business)"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-1">Business Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Acme Corp"
                required
              />
            </div>
            <div>
              <label htmlFor="slug" className="block text-sm font-medium mb-1">Slug *</label>
              <input
                id="slug"
                type="text"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="acme-corp"
                required
                pattern="^[a-z0-9-]+$"
              />
              <p className="mt-1 text-sm text-muted-foreground">URL-friendly identifier (lowercase, numbers, hyphens only)</p>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium mb-1">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Brief description of this client business"
                rows={3}
              />
            </div>
          </div>
        </Section>

        {/* GHL Credentials */}
        <Section
          title="GoHighLevel (GHL) Credentials"
          icon={Bot}
          description="Connect this client’s GoHighLevel location and credentials."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="ghl_location_id" className="block text-sm font-medium mb-1">Location ID *</label>
              <input
                id="ghl_location_id"
                type="text"
                value={formData.ghl_location_id}
                onChange={(e) => handleChange('ghl_location_id', e.target.value)}
                className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="abc123xyz"
              />
            </div>
            <SecretInput
              label="API Key *"
              value={formData.ghl_api_key}
              onChange={(v) => handleChange('ghl_api_key', v)}
              showPassword={showGhlApiKey}
              onToggleShow={() => setShowGhlApiKey(!showGhlApiKey)}
              placeholder="pit-xxx or pat-xxx"
              helpText="PIT (Partner Integration Token) or PAT from GHL Agency Settings"
            />
            <SecretInput
              label="PIT (Partner Integration Token)"
              value={formData.ghl_pit}
              onChange={(v) => handleChange('ghl_pit', v)}
              showPassword={showGhlPit}
              onToggleShow={() => setShowGhlPit(!showGhlPit)}
              placeholder="pit-xxx"
              helpText="Optional: Required for some GHL API operations"
            />
            <div>
              <label htmlFor="ghl_custom_field_form_submitted" className="block text-sm font-medium mb-1">Form Submitted Custom Field</label>
              <input
                id="ghl_custom_field_form_submitted"
                type="text"
                value={formData.ghl_custom_field_form_submitted}
                onChange={(e) => handleChange('ghl_custom_field_form_submitted', e.target.value)}
                className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="form_submitted"
              />
            </div>
            <div>
              <label htmlFor="ghl_custom_field_ig_username" className="block text-sm font-medium mb-1">IG Username Custom Field</label>
              <input
                id="ghl_custom_field_ig_username"
                type="text"
                value={formData.ghl_custom_field_ig_username}
                onChange={(e) => handleChange('ghl_custom_field_ig_username', e.target.value)}
                className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="instagram_username"
              />
            </div>
          </div>
        </Section>

        {/* Bot Configuration */}
        <Section
          title="Bot Configuration"
          icon={Bot}
          description="Customize the AI agent behavior for this business"
        >
          <div className="space-y-6">
            <div className="grid gap-5 lg:grid-cols-2">
              <div>
                <label htmlFor="agent_goal" className="block text-sm font-medium mb-1">Agent goal</label>
                <textarea id="agent_goal" value={formData.agent_goal} onChange={(e) => handleChange('agent_goal', e.target.value)} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" placeholder="What outcome should the agent work toward for this client?" rows={4} />
                <p className="mt-1 text-sm text-muted-foreground">The client-specific objective included in every Hermes task.</p>
              </div>
              <div>
                <label htmlFor="agent_behavior" className="block text-sm font-medium mb-1">Behavior rules</label>
                <textarea id="agent_behavior" value={formData.agent_behavior} onChange={(e) => handleChange('agent_behavior', e.target.value)} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Tone, boundaries, when to ask a question, and when to hand off to a person." rows={4} />
                <p className="mt-1 text-sm text-muted-foreground">Set how the agent communicates and what it must escalate.</p>
              </div>
            </div>
            <div>
              <label htmlFor="bot_context" className="block text-sm font-medium mb-1">Business knowledge base</label>
              <textarea id="bot_context" value={formData.bot_context} onChange={(e) => handleChange('bot_context', e.target.value)} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Add services, audience, pricing or offer details, FAQs, policies, approved claims, and business facts that apply across campaigns." rows={7} />
              <p className="mt-1 text-sm text-muted-foreground">Client-specific facts and training. This is combined with the goal, behavior rules, matched campaign instructions, and conversation history.</p>
            </div>
            <div className="border-t border-border pt-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4"><div><h4 className="font-medium">Campaign triggers and instructions</h4><p className="mt-1 text-sm text-muted-foreground">Match an incoming lead message to a campaign. The AI uses its instructions together with the global business context.</p></div><button type="button" onClick={() => setFormData(prev => ({ ...prev, campaign_configs: [...prev.campaign_configs, { trigger_phrase: '', ai_context: '', form_url: '', ghl_form_id: '' }] }))} className="px-3 py-2 text-sm border border-input rounded-lg hover:bg-muted">Add campaign</button></div>
              {formData.campaign_configs.length === 0 ? <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">No campaign triggers added yet.</div> : <div className="space-y-4">{formData.campaign_configs.map((campaign, index) => {
                const updateCampaign = (field: keyof CampaignConfig, value: string) => setFormData(prev => ({ ...prev, campaign_configs: prev.campaign_configs.map((item, i) => i === index ? { ...item, [field]: value } : item) }));
                return <div key={index} className="rounded-xl border border-border p-4 space-y-4"><div className="flex items-center justify-between"><h5 className="font-medium">Campaign {index + 1}</h5><button type="button" onClick={() => setFormData(prev => ({ ...prev, campaign_configs: prev.campaign_configs.filter((_, i) => i !== index) }))} className="text-sm text-destructive">Remove</button></div>
                  <div><label className="block text-sm font-medium mb-1">Incoming lead context phrase</label><input type="text" value={campaign.trigger_phrase} onChange={e => updateCampaign('trigger_phrase', e.target.value)} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Thanks for reaching out, saw your ad and want more info" /><p className="mt-1 text-sm text-muted-foreground">The incoming message or phrase that identifies this campaign.</p></div>
                  <div><label className="block text-sm font-medium mb-1">Campaign AI instructions</label><textarea value={campaign.ai_context} onChange={e => updateCampaign('ai_context', e.target.value)} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" rows={3} placeholder="Describe this campaign’s offer, intent, response guidance, and relevant context." /></div>
                  <div className="grid gap-4 md:grid-cols-2"><div><label className="block text-sm font-medium mb-1">Form URL sent to leads</label><input type="url" value={campaign.form_url} onChange={e => updateCampaign('form_url', e.target.value)} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" placeholder="https://forms.gohighlevel.com/..." /></div><div><label className="block text-sm font-medium mb-1">GHL Form ID</label><input type="text" value={campaign.ghl_form_id} onChange={e => updateCampaign('ghl_form_id', e.target.value)} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Form ID used to verify completion" /></div></div>
                </div>;
              })}</div>}
            </div>
            <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground"><strong className="text-foreground">Dynamic qualification</strong><p className="mt-1">There is no fixed set of qualification questions. The AI can decide what to ask from the global business context and the matched campaign instructions.</p></div>
          </div>
        </Section>

        {/* Behavior Settings */}
        <Section
          title="Behavior Settings"
          icon={MessageSquare}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="lead_timeout_minutes" className="block text-sm font-medium mb-1">Lead Timeout (minutes)</label>
              <input
                id="lead_timeout_minutes"
                type="number"
                value={formData.lead_timeout_minutes}
                onChange={(e) => handleChange('lead_timeout_minutes', parseInt(e.target.value) || 60)}
                className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                min="1"
                max="1440"
              />
              <p className="mt-1 text-sm text-muted-foreground">Mark lead as stale after this many minutes of inactivity</p>
            </div>
            <div>
              <label htmlFor="max_messages_per_run" className="block text-sm font-medium mb-1">Max Messages Per Run</label>
              <input
                id="max_messages_per_run"
                type="number"
                value={formData.max_messages_per_run}
                onChange={(e) => handleChange('max_messages_per_run', parseInt(e.target.value) || 50)}
                className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                min="1"
                max="200"
              />
              <p className="mt-1 text-sm text-muted-foreground">Rate limit safety: max messages sent per 2-minute cron run</p>
            </div>
          </div>
        </Section>

        {/* Submit button at bottom */}
        <div className="flex justify-end pt-4 border-t border-border">
          <button
            type="submit"
            disabled={saving}
            className="px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-lg font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader size="sm" />
                <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                <span>{isEditing ? 'Update Sub-Account' : 'Create Sub-Account'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}